import React, { type ReactNode, useCallback, useEffect, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { HorizontalScrollContainer } from '@atlassian/conversation-assistant-ui-components';
import { PersonCard } from '@atlassian/people-teams-ui/directory';
import { useActivityClient } from '@atlassian/recent-work-client';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import {
	NLPSearchErrorState,
	type NLPSearchPersonResultDataType,
	NLPSearchResultFormat,
	type NLPSearchWhoResultDataType,
} from '../../ai-answer-dialog/types';
import { getTypedJSONResultData } from '../../ai-answer-dialog/utils/getTypedJSONResultData';
import { logger } from '../../common';
import { useAnalytics } from '../../common/controller/analytics';
import { KnowledgeCarousel } from '../../knowledge-card-components';
import { AIDisclaimerAndFeedback } from '../../v2/common/ui/ai-disclaimer-and-feedback';
import { onKnowledgeCardShown } from '../analytics';
import { fetchPrimaryUserData } from '../gql/hydrate-people-card';
import { KnowledgeCardLoading } from '../knowledge-card-loading';
import { KnowledgeCardsBody } from '../knowledge-cards-body';
import { KnowledgeCardsHeader } from '../knowledge-cards-header';
import { SeparatorLine, SeparatorLineRefresh } from '../separator-line';
import { KnowledgeCardsIntentType } from '../types';
import { formatTimeKnowledgeCard } from '../util';

import { PersonCardBody } from './person-card-body';
import { PersonCardHeader } from './person-card-header';

const ScrollableContainerStyles = cssMap({
	default: {
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		margin: 'auto',
		marginBottom: token('space.100'),
		display: 'flex',
	},
});

const CardContainerStyles = cssMap({
	default: {
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
	},
	refresh: {
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
		gap: token('space.200'),
	},
	disclaimer: {
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
});

const PersonCardWrapperStyles = cssMap({
	default: {
		width: '180px',
		textOverflow: 'ellipsis',
		whiteSpace: 'normal',
	},
	flex: {
		flexBasis: '180px',
		flexGrow: 0,
		flexShrink: 0,
	},
});

type IndividualPersonKnowledgeCardProps = {
	primaryUser: NLPSearchWhoResultDataType | undefined;
};

const USERS_SCROLL_OFFSET = 392;

const messages = defineMessages({
	multiple_people_matches_header: {
		id: 'search_ai.knowledge_cards.multiple_people_matches_header',
		defaultMessage: 'People matches',
		description: 'Header for the multiple people carousel',
	},
	ai_answer_component_topics_not_relevant_checkbox_label: {
		id: 'search_ai.ai_answer_component_topics_not_relevant_checkbox_label',
		defaultMessage: 'The topics are not relevant to this person.',
		description:
			'Label for the checkbox asking if related topics shown for the person are relevant',
	},
});

const IndividualPersonKnowledgeCard = ({ primaryUser }: IndividualPersonKnowledgeCardProps) => {
	if (!primaryUser) {
		return null;
	}

	const showKnowledgeCardBody =
		(primaryUser?.recentActivity?.length || 0) > 0 || (primaryUser?.collaborators?.length || 0) > 0;

	return fg('kd_people_cards_update') ? (
		<>
			<Stack xcss={CardContainerStyles.refresh} testId="individual-person-knowledge-card">
				<PersonCardHeader
					accountId={primaryUser.account_id}
					name={primaryUser.name}
					email={primaryUser.email}
					avatarUrl={primaryUser.avatar_url}
					profileUrl={primaryUser.profile_url}
					jobTitle={primaryUser.job_title}
					department={primaryUser.department}
					location={primaryUser.location}
					time={primaryUser.time}
				/>

				{showKnowledgeCardBody && <SeparatorLineRefresh />}
				<Box>
					{showKnowledgeCardBody && (
						<PersonCardBody
							currentUserId={primaryUser.account_id ?? ''}
							recommendedUsers={primaryUser.collaborators || []}
							recentActivity={primaryUser.recentActivity || []}
							profileUrl={primaryUser.profile_url || ''}
						/>
					)}
					<Box xcss={CardContainerStyles.disclaimer}>
						<AIDisclaimerAndFeedback showFeedbackButtons />
					</Box>
				</Box>
			</Stack>
		</>
	) : (
		<>
			<Stack xcss={CardContainerStyles.default}>
				<KnowledgeCardsHeader
					type={KnowledgeCardsIntentType.PERSON}
					accountId={primaryUser.account_id}
					name={primaryUser.name}
					email={primaryUser.email}
					avatarUrl={primaryUser.avatar_url}
					profileUrl={primaryUser.profile_url}
					jobTitle={primaryUser.job_title}
					department={primaryUser.department}
					location={primaryUser.location}
					time={primaryUser.time}
				/>
				<SeparatorLine />
				<KnowledgeCardsBody
					type={KnowledgeCardsIntentType.PERSON}
					currentUserId={primaryUser.account_id ?? ''}
					recommendedUsers={primaryUser.collaborators || []}
					recentActivity={primaryUser.recentActivity || []}
					profileUrl={primaryUser.profile_url || ''}
				/>
			</Stack>
		</>
	);
};

export const PersonKnowledgeCard = ({ onFMPReached }: { onFMPReached?: () => void }) => {
	const {
		commonAttributes,
		userDetails: { id: requestingUserId },
		setData,
		sourceProduct,
		loading,
	} = useAIAnswerContext();
	const { formatMessage, locale } = useIntl();
	const activityClient = useActivityClient('v3', 'people');
	const [fireAnalyticsEvent] = useAnalytics();
	const [selectedUser, setSelectedUser] = useState<number | undefined>();
	const [primaryUser, setPrimaryUser] = useState<NLPSearchWhoResultDataType | undefined>();
	const { answerString, cloudId, workspaceId } = commonAttributes;
	const [isPersonCardLoading, setIsPersonCardLoading] = useState(false);

	const userResults: NLPSearchPersonResultDataType[] = getTypedJSONResultData(answerString)
		.parsedResult as NLPSearchPersonResultDataType[];

	const ScrollWrapper =
		userResults.length > 3
			? (props: { children: ReactNode }) => (
					<HorizontalScrollContainer>
						<Box xcss={ScrollableContainerStyles.default}>{props.children}</Box>
					</HorizontalScrollContainer>
				)
			: React.Fragment;

	const getHandleSelectUser = useCallback(
		(index: number) => () => {
			setSelectedUser(index);
		},
		[],
	);

	useEffect(() => {
		if (userResults.length === 1) {
			setSelectedUser(0);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userResults]);

	useEffect(() => {
		const fetchData = async () => {
			if (selectedUser !== undefined) {
				setIsPersonCardLoading(true);
				return fetchPrimaryUserData({
					accountId: userResults[selectedUser].id,
					tenantId: cloudId,
					requestingUserId: requestingUserId || '',
					activityClient,
					locale,
					workspaceId,
					product: sourceProduct ?? '',
				})
					.then((data) => {
						setPrimaryUser(data);
					})
					.catch((error) => {
						setData({
							errorState: NLPSearchErrorState.NetworkError,
							disclaimer: null,
							format: NLPSearchResultFormat.JSON,
							nlpResults: [],
							uniqueSources: [],
							nlpFollowUpResults: null,
						});
						if (fg('kd_enable_networkerror_logging')) {
							logger.error({
								error,
								functionName: 'fetchPrimaryUserData: PersonKnowledgeCard',
							});
						}
					})
					.finally(() => {
						setIsPersonCardLoading(false);
					});
			}
		};
		fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedUser]);

	useEffect(() => {
		onFMPReached?.();
		fireAnalyticsEvent(
			onKnowledgeCardShown({
				...commonAttributes,
				cardType: KnowledgeCardsIntentType.PERSON,
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if ((loading || isPersonCardLoading) && fg('kd_people_cards_update')) {
		return <KnowledgeCardLoading />;
	}

	if (userResults.length === 0) {
		return null;
	}

	if (selectedUser !== undefined || userResults.length === 1) {
		return <IndividualPersonKnowledgeCard primaryUser={primaryUser} />;
	}

	if (fg('kd_people_cards_update')) {
		return (
			<>
				{fg('kd_smart_answers_update_person_carousel') ? (
					<Stack space="space.150">
						<KnowledgeCarousel
							heading={formatMessage(messages.multiple_people_matches_header)}
							scrollOffset={USERS_SCROLL_OFFSET}
						>
							{userResults.map((user, index) => (
								// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
								<Box
									onClick={getHandleSelectUser(index)}
									xcss={cx(PersonCardWrapperStyles.default, PersonCardWrapperStyles.flex)}
									testId="person-knowledge-card-wrapper"
									key={user.id}
								>
									<PersonCard
										data={{
											id: user.id,
											name: user.name,
											jobTitle: user.title ?? '',
											avatarUrl: user.avatarUrl,
											localTime: formatTimeKnowledgeCard({ locale, zoneInfo: user.zoneInfo }),
											location: user.location ?? '',
										}}
									/>
								</Box>
							))}
						</KnowledgeCarousel>
						<AIDisclaimerAndFeedback showFeedbackButtons />
					</Stack>
				) : (
					<Stack space="space.150">
						<ScrollWrapper>
							<Inline space="space.200">
								{userResults.map((user, index) => (
									// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
									<Box
										onClick={getHandleSelectUser(index)}
										xcss={PersonCardWrapperStyles.default}
										testId="person-knowledge-card-wrapper"
										key={user.id}
									>
										<PersonCard
											data={{
												id: user.id,
												name: user.name,
												jobTitle: user.title ?? '',
												avatarUrl: user.avatarUrl,
												localTime: formatTimeKnowledgeCard({ locale, zoneInfo: user.zoneInfo }),
												location: user.location ?? '',
											}}
										/>
									</Box>
								))}
							</Inline>
						</ScrollWrapper>
						<AIDisclaimerAndFeedback showFeedbackButtons />
					</Stack>
				)}
			</>
		);
	}
	return (
		<>
			{/* adds horizontal scroll when more than 3 users */}
			{fg('kd_smart_answers_update_person_carousel') ? (
				<KnowledgeCarousel
					heading={formatMessage(messages.multiple_people_matches_header)}
					scrollOffset={USERS_SCROLL_OFFSET}
				>
					{userResults.map((user, index) => (
						// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
						<Box
							onClick={getHandleSelectUser(index)}
							xcss={cx(PersonCardWrapperStyles.default, PersonCardWrapperStyles.flex)}
							testId="person-knowledge-card-wrapper"
							key={user.id}
						>
							<PersonCard
								data={{
									id: user.id,
									name: user.name,
									jobTitle: user.title ?? '',
									avatarUrl: user.avatarUrl,
									localTime: formatTimeKnowledgeCard({ locale, zoneInfo: user.zoneInfo }),
									location: user.location ?? '',
								}}
							/>
						</Box>
					))}
				</KnowledgeCarousel>
			) : (
				<ScrollWrapper>
					<Inline space="space.200">
						{userResults.map((user, index) => (
							// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
							<Box
								onClick={getHandleSelectUser(index)}
								xcss={PersonCardWrapperStyles.default}
								testId="person-knowledge-card-wrapper"
								key={user.id}
							>
								<PersonCard
									data={{
										id: user.id,
										name: user.name,
										jobTitle: user.title ?? '',
										avatarUrl: user.avatarUrl,
										localTime: formatTimeKnowledgeCard({ locale, zoneInfo: user.zoneInfo }),
										location: user.location ?? '',
									}}
								/>
							</Box>
						))}
					</Inline>
				</ScrollWrapper>
			)}
		</>
	);
};
